import { Box, Stack, Tooltip, Typography } from '@mui/material';
import { useTheme } from '@mui/system';
import { truncate } from 'lodash';
import { useEffect, useRef, useState } from 'react';

interface ChartResponsiveAxisTickProps {
  charLength?: number;
  textAnchor?: string;
  fontSize?: string | number;
  shouldDisableTooltip?: boolean;
  shouldBreakTickText?: boolean;
  shouldTruncate?: boolean;
  shouldAdjustFontSize?: boolean;
  shouldAdjustAnchorForStartEnd?: boolean;
  ticks?: {
    startIndex: number;
    endIndex: number;
    totalCount: number;
  };
  [rechartProp: string]: any;
  highlightTick?: {
    index: number;
    color: string;
  };
  onMouseEnter?: (event: React.MouseEvent, index: number) => void;
  onMouseLeave?: (event: React.MouseEvent) => void;
}

export function ChartResponsiveAxisTick({
  charLength = 14,
  textAnchor = 'end',
  fontSize,
  shouldDisableTooltip = false,
  shouldBreakTickText = true,
  shouldTruncate = true,
  shouldAdjustFontSize = false,
  shouldAdjustAnchorForStartEnd = false,
  ticks,
  highlightTick,
  onMouseEnter,
  onMouseLeave,
  ...rechartsProps
}: ChartResponsiveAxisTickProps) {
  const theme = useTheme();
  const textRef = useRef<SVGTextElement>(null);
  const [textWidth, setTextWidth] = useState(0);

  const {
    x,
    y,
    payload,
    width,
    visibleTicksCount,
    index,
    getLabelOptions,
    tickFormatter,
  } = rechartsProps;

  const getTickData = () => {
    if (!tickFormatter) {
      return {
        displayLabel: payload.value,
        labelText: undefined,
        labelIcon: undefined,
      };
    }

    try {
      const stringifiedTickValue = tickFormatter(payload.value);
      const tickValue = JSON.parse(stringifiedTickValue);
      const labelOptions =
        getLabelOptions && getLabelOptions({ id: tickValue.id, size: 12 });

      return {
        displayLabel: tickValue.label,
        labelText: labelOptions.label,
        labelIcon: labelOptions.icon,
      };
    } catch {
      return {
        displayLabel: payload.value,
        labelText: undefined,
        labelIcon: undefined,
      };
    }
  };

  const { displayLabel, labelIcon, labelText } = getTickData();

  useEffect(() => {
    if (textRef.current) {
      setTextWidth(textRef.current.getComputedTextLength());
    }
  }, [displayLabel]);

  const startIndex = ticks?.startIndex ?? 0;
  const endIndex = ticks?.endIndex ?? 0;
  const totalTickCount = ticks?.totalCount ?? 0;

  const getTextAnchor = () => {
    if (!shouldAdjustAnchorForStartEnd) {
      return textAnchor;
    }
    if (index === 0) {
      return startIndex === 0 ? 'start' : startIndex > 0 ? 'middle' : 'start';
    }
    if (index === visibleTicksCount - 1) {
      return endIndex === totalTickCount - 1
        ? 'end'
        : endIndex < totalTickCount - 1
        ? 'middle'
        : 'end';
    }
    return textAnchor;
  };

  const getFontSize = () => {
    if (fontSize) {
      return fontSize;
    }
    if (shouldAdjustFontSize && width / visibleTicksCount <= 80) {
      return '0.65rem';
    }
    return undefined; // default fontSize
  };

  const renderTypographyNode = (value: string, dy?: number, key?: number) => {
    return (
      <Typography
        key={key}
        ref={textRef}
        component="text"
        variant="caption"
        x={0}
        y={0}
        dy={dy || 6}
        textAnchor={getTextAnchor()}
        fill={
          highlightTick?.index === index
            ? highlightTick?.color
            : theme.palette.text.secondary
        }
        fontSize={getFontSize()}
      >
        {shouldTruncate ? truncate(value, { length: charLength }) : value}
      </Typography>
    );
  };

  const hasNewLineChar =
    typeof displayLabel === 'string' && displayLabel.includes('\n');

  const renderAxisTick = () => (
    <Box
      component="g"
      transform={`translate(${x},${y})`}
      onMouseEnter={(event) => onMouseEnter?.(event, index)}
      onMouseLeave={onMouseLeave}
    >
      {labelIcon && (
        <Box
          component="foreignObject"
          x={-textWidth - 16}
          y={-12}
          width={20}
          height={20}
        >
          {labelIcon}
        </Box>
      )}
      {hasNewLineChar && shouldBreakTickText
        ? displayLabel
            .split('\n')
            .map((value: string, index: number) =>
              renderTypographyNode(value, index * 20, index),
            )
        : renderTypographyNode(displayLabel.toString().replace('\0', ' - '))}
    </Box>
  );

  if (!onMouseEnter || !onMouseLeave) {
    const tooltipTitle = (
      <Stack direction="row" spacing={0.5} alignItems="start">
        <Box>{labelIcon}</Box>
        <Typography variant="caption">
          {labelText && `${labelText}: `}
          {displayLabel}
        </Typography>
      </Stack>
    );

    return (
      <Tooltip
        title={shouldDisableTooltip ? '' : tooltipTitle}
        placement="top"
        disableInteractive
      >
        {renderAxisTick()}
      </Tooltip>
    );
  }

  return renderAxisTick();
}
